import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Globe, User as UserIcon, Mail, Phone, Camera, Check, Link2, Unlink, ShieldCheck, AlertTriangle, X, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang, LANGUAGES } from "@/lib/lang";
import api, { API } from "@/lib/api";

export default function AccountPage() {
    const { user, refresh, logout } = useAuth();
    const { lang, setLang } = useLang();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        picture: "",
    });
    const [saving, setSaving] = useState(false);
    const [picturePreview, setPicturePreview] = useState("");
    const [linkingGoogle, setLinkingGoogle] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [unlinkOpen, setUnlinkOpen] = useState(false);
    const [working, setWorking] = useState(false);

    // Handle ?linked=google|error callback from OAuth flow
    useEffect(() => {
        const linked = searchParams.get("linked");
        if (linked === "google") {
            toast.success("Compte Google lié avec succès !");
            searchParams.delete("linked");
            setSearchParams(searchParams, { replace: true });
        } else if (linked === "error") {
            toast.error("Impossible de lier ce compte Google. Il est peut-être déjà utilisé ailleurs.");
            searchParams.delete("linked");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleLinkGoogle = () => {
        setLinkingGoogle(true);
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        localStorage.setItem("tk_oauth_intent", "link-google");
        const redirectUrl = window.location.origin + "/app/account";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleUnlinkGoogleConfirm = async () => {
        setWorking(true);
        try {
            await api.post("/auth/unlink-google");
            await refresh?.();
            setUnlinkOpen(false);
            toast.success("Compte Google déconnecté");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur lors de la déconnexion");
        } finally {
            setWorking(false);
        }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem("tk_token");
            const res = await fetch(`${API}/users/me/export`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!res.ok) throw new Error("export failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ticketify-export-${user?.user_id || "data"}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export téléchargé");
        } catch {
            toast.error("Échec de l'export");
        }
    };

    const handleResetConfirm = async () => {
        setWorking(true);
        try {
            const { data } = await api.post("/users/me/reset");
            const d = data?.deleted || {};
            setResetOpen(false);
            toast.success(
                `Données réinitialisées : ${d.receipts || 0} reçu(s) · ${d.transactions || 0} transaction(s) · ${d.email_connections || 0} connexion(s) supprimés.`,
                { duration: 6000 },
            );
            await refresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur de réinitialisation");
        } finally {
            setWorking(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setWorking(true);
        try {
            await api.delete("/users/me");
            setDeleteOpen(false);
            toast.success("Compte supprimé");
            await logout?.();
            navigate("/accueil");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur de suppression");
        } finally {
            setWorking(false);
        }
    };

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || "",
                email: user.email || "",
                phone: user.phone || "",
                picture: user.picture || "",
            });
            setPicturePreview(user.picture || "");
        }
    }, [user]);

    const handlePictureChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image trop lourde (max 2 Mo)");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = ev.target.result;
            setPicturePreview(dataUrl);
            setForm((f) => ({ ...f, picture: dataUrl }));
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.patch("/users/me", {
                name: form.name,
                phone: form.phone,
                picture: form.picture,
            });
            await refresh?.();
            toast.success("Profil mis à jour");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const initial = (form.name?.[0] || form.email?.[0] || "U").toUpperCase();

    return (
        <div className="space-y-8">
            <header>
                <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Profil</h2>
                <p className="mt-1 text-sm text-[#86868B]">
                    Mettez à jour vos informations personnelles et votre photo de profil.
                </p>
            </header>

            {/* Avatar */}
            <section className="tk-card p-6">
                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-4">
                    Photo de profil
                </p>
                <div className="flex items-center gap-6">
                    {picturePreview ? (
                        <img
                            src={picturePreview}
                            alt=""
                            className="w-20 h-20 rounded-full object-cover border border-black/5"
                            data-testid="account-avatar-preview"
                        />
                    ) : (
                        <div className="w-20 h-20 rounded-full bg-[#1D1D1F] text-white flex items-center justify-center text-2xl font-semibold">
                            {initial}
                        </div>
                    )}
                    <label className="tk-btn tk-btn-secondary cursor-pointer">
                        <Camera className="w-4 h-4" />
                        Changer la photo
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handlePictureChange}
                            className="hidden"
                            data-testid="account-picture-input"
                        />
                    </label>
                </div>
                <p className="mt-3 text-xs text-[#86868B]">JPG, PNG ou GIF. Max 2 Mo.</p>
            </section>

            {/* Form */}
            <section className="tk-card p-6 space-y-5">
                <Field icon={UserIcon} label="Nom complet" data-testid="account-name">
                    <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="tk-input"
                        data-testid="account-name-input"
                    />
                </Field>
                <Field icon={Mail} label="E-mail">
                    <input
                        type="email"
                        value={form.email}
                        readOnly
                        className="tk-input opacity-70 cursor-not-allowed"
                        data-testid="account-email-input"
                    />
                    <p className="mt-2 text-xs text-[#86868B]">
                        L&apos;e-mail ne peut pas être modifié. Contactez le support pour cela.
                    </p>
                </Field>
                <Field icon={Phone} label="Numéro de téléphone">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+33 6 12 34 56 78"
                        className="tk-input"
                        data-testid="account-phone-input"
                    />
                </Field>
                <Field icon={UserIcon} label="Forfait personnel">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-[#F5F5F7] border border-black/5">
                        <span className="text-sm font-medium capitalize text-[#1D1D1F]">
                            Plan {user?.plan || "free"}
                        </span>
                        <a
                            href="/app/forfait"
                            className="text-xs font-semibold text-[#007AFF] hover:underline"
                        >
                            Changer →
                        </a>
                    </div>
                </Field>
            </section>

            {/* Language */}
            <section className="tk-card p-6">
                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-4 flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5" /> Langue de l&apos;interface
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                    {LANGUAGES.map((l) => {
                        const active = lang === l.code;
                        return (
                            <button
                                key={l.code}
                                onClick={() => setLang(l.code)}
                                data-testid={`account-lang-${l.code}`}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition ${
                                    active
                                        ? "bg-[#1D1D1F] text-white border-[#1D1D1F]"
                                        : "bg-white text-[#1D1D1F] border-black/5 hover:bg-[#F5F5F7]"
                                }`}
                            >
                                <span className="text-lg">{l.flag}</span>
                                <span className="text-sm flex-1 text-left">{l.label}</span>
                                {active && <Check className="w-4 h-4" />}
                            </button>
                        );
                    })}
                </div>
                <p className="mt-3 text-xs text-[#86868B]">
                    Préférence enregistrée. Les traductions complètes arrivent bientôt.
                </p>
            </section>

            {/* Save */}
            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="tk-btn tk-btn-primary"
                    data-testid="account-save"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>

            {/* Connected accounts */}
            <section className="tk-card p-6" data-testid="linked-accounts-section">
                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Comptes liés
                </p>

                <div className="space-y-3">
                    {/* Email/password method */}
                    <div className="flex items-center gap-4 p-4 rounded-xl border border-black/5 bg-[#F5F5F7]">
                        <div className="w-10 h-10 rounded-xl bg-white border border-black/5 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-[#1D1D1F]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1D1D1F]">Email et mot de passe</p>
                            <p className="text-xs text-[#86868B] truncate">
                                {user?.has_password
                                    ? `Activé · ${user?.email}`
                                    : "Aucun mot de passe défini — utilisez « Mot de passe oublié »."}
                            </p>
                        </div>
                        <span className={`text-xs font-semibold ${user?.has_password ? "text-[#34C759]" : "text-[#86868B]"}`}>
                            {user?.has_password ? "✓ Actif" : "Inactif"}
                        </span>
                    </div>

                    {/* Google */}
                    {user?.auth_providers?.includes("google") ? (
                        <div className="flex items-center gap-4 p-4 rounded-xl border border-[#34C759]/25 bg-[#34C759]/5" data-testid="google-linked-row">
                            <div className="w-10 h-10 rounded-xl bg-white border border-black/5 flex items-center justify-center">
                                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-[#1D1D1F]">Google</p>
                                <p className="text-xs text-[#86868B] truncate">
                                    Lié à <strong>{user?.google_email || user?.email}</strong>
                                </p>
                            </div>
                            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-[#34C759]">
                                <Check className="w-3.5 h-3.5" /> Connecté
                            </span>
                            {user?.has_password && (
                                <button
                                    onClick={() => setUnlinkOpen(true)}
                                    className="tk-btn tk-btn-secondary text-xs"
                                    data-testid="unlink-google-btn"
                                >
                                    <Unlink className="w-3.5 h-3.5" /> Délier
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-4 p-4 rounded-xl border border-black/5 bg-white" data-testid="google-unlinked-row">
                            <div className="w-10 h-10 rounded-xl bg-[#F5F5F7] border border-black/5 flex items-center justify-center">
                                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-[#1D1D1F]">Google</p>
                                <p className="text-xs text-[#86868B]">Connexion en un clic depuis n&apos;importe quel appareil.</p>
                            </div>
                            <button
                                onClick={handleLinkGoogle}
                                disabled={linkingGoogle}
                                className="tk-btn tk-btn-primary text-xs"
                                data-testid="link-google-btn"
                            >
                                <Link2 className="w-3.5 h-3.5" />
                                {linkingGoogle ? "Redirection…" : "Lier Google"}
                            </button>
                        </div>
                    )}
                </div>

                <p className="mt-4 text-xs text-[#86868B] leading-relaxed">
                    Lier votre compte Google permet de vous connecter en un clic, sans saisir votre mot de passe.
                    Vous gardez l&apos;accès email/mot de passe en parallèle.
                </p>
            </section>

            {/* RGPD — danger zone */}
            <section className="tk-card p-6 mt-6 border border-[#FF3B30]/15">
                <p className="text-xs font-bold uppercase tracking-wider text-[#FF3B30]">
                    Mes données (RGPD)
                </p>
                <h3 className="mt-1 text-lg font-semibold text-[#1D1D1F]">Export et suppression</h3>
                <p className="mt-2 text-sm text-[#86868B]">
                    Conformément aux articles 17 et 20 du RGPD, vous pouvez télécharger toutes vos données
                    Ticketify au format JSON et/ou demander la suppression de votre compte à tout moment.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3 flex-wrap">
                    <button
                        onClick={handleExport}
                        className="tk-btn tk-btn-secondary"
                        data-testid="account-export-data"
                    >
                        Exporter mes données
                    </button>
                    <button
                        onClick={() => setResetOpen(true)}
                        className="tk-btn text-[#FF9F0A] border border-[#FF9F0A]/30 hover:bg-[#FF9F0A]/5"
                        data-testid="account-reset-data"
                    >
                        Réinitialiser mes données
                    </button>
                    <button
                        onClick={() => setDeleteOpen(true)}
                        className="tk-btn text-[#FF3B30] border border-[#FF3B30]/20 hover:bg-[#FF3B30]/5"
                        data-testid="account-delete-account"
                    >
                        Supprimer mon compte
                    </button>
                </div>
                <p className="mt-3 text-xs text-[#86868B]">
                    Les justificatifs comptables sont anonymisés et conservés 10 ans (art. L102 B LPF) — obligation légale française.
                </p>
            </section>

            {/* Modals */}
            <DangerModal
                open={resetOpen}
                onClose={() => !working && setResetOpen(false)}
                onConfirm={handleResetConfirm}
                working={working}
                tone="warning"
                title="Réinitialiser toutes vos données ?"
                phrase="RESET"
                ctaLabel="Tout réinitialiser"
                testid="reset-modal"
                bullets={[
                    "Tous vos tickets & justificatifs seront supprimés",
                    "Toutes vos transactions bancaires importées seront supprimées",
                    "Toutes vos connexions email & banque seront déconnectées",
                    "Votre historique de paiements sera vidé",
                ]}
                note="Votre compte et votre plan sont conservés. Action irréversible."
            />

            <DangerModal
                open={deleteOpen}
                onClose={() => !working && setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                working={working}
                tone="danger"
                title="Supprimer définitivement votre compte ?"
                phrase="SUPPRIMER"
                ctaLabel="Supprimer mon compte"
                testid="delete-modal"
                bullets={[
                    "Votre profil et vos identifiants seront effacés",
                    "Vos connexions Gmail / banques seront révoquées",
                    "Vous serez immédiatement déconnecté",
                ]}
                note="Vos justificatifs comptables seront anonymisés mais conservés 10 ans (art. L102 B LPF). Action irréversible."
            />

            <UnlinkGoogleModal
                open={unlinkOpen}
                onClose={() => !working && setUnlinkOpen(false)}
                onConfirm={handleUnlinkGoogleConfirm}
                working={working}
            />
        </div>
    );
}

function Field({ icon: Icon, label, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-2 flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" /> {label}
            </label>
            {children}
        </div>
    );
}

function DangerModal({ open, onClose, onConfirm, working, tone, title, phrase, ctaLabel, bullets, note, testid }) {
    const [value, setValue] = useState("");
    useEffect(() => {
        if (!open) setValue("");
    }, [open]);
    if (!open) return null;
    const isWarn = tone === "warning";
    const accent = isWarn ? "#FF9F0A" : "#FF3B30";
    const matches = value.trim().toUpperCase() === phrase;
    return (
        <div
            className="fixed inset-0 z-[90] flex items-center justify-center px-4 sm:px-6"
            role="dialog"
            aria-modal="true"
            data-testid={testid}
        >
            <button
                aria-label="Fermer"
                onClick={onClose}
                className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            />
            <div className="relative tk-card max-w-md w-full p-6 sm:p-7 tk-menu-pop">
                <button
                    onClick={onClose}
                    disabled={working}
                    className="absolute top-4 right-4 text-[#86868B] hover:text-[#1D1D1F] disabled:opacity-40"
                    aria-label="Fermer"
                    data-testid={`${testid}-close`}
                >
                    <X className="w-5 h-5" />
                </button>

                <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: `${accent}15`, color: accent }}
                >
                    <AlertTriangle className="w-5 h-5" />
                </div>

                <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">{title}</h3>

                <ul className="mt-4 space-y-1.5 text-sm text-[#1D1D1F]/85">
                    {bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-[#1D1D1F]/40 shrink-0" />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>

                <p className="mt-4 text-xs text-[#86868B] leading-relaxed">{note}</p>

                <label className="block mt-5 text-xs font-bold uppercase tracking-wider text-[#86868B]">
                    Tapez <code className="font-mono px-1.5 py-0.5 rounded bg-[#F5F5F7] text-[#1D1D1F]">{phrase}</code> pour confirmer
                </label>
                <input
                    autoFocus
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={phrase}
                    className="tk-input mt-2 font-mono"
                    data-testid={`${testid}-input`}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && matches && !working) onConfirm();
                    }}
                />

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={working}
                        className="tk-btn tk-btn-secondary"
                        data-testid={`${testid}-cancel`}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={!matches || working}
                        className="tk-btn text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: accent }}
                        data-testid={`${testid}-confirm`}
                    >
                        {working ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" /> En cours…
                            </>
                        ) : (
                            ctaLabel
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function UnlinkGoogleModal({ open, onClose, onConfirm, working }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[90] flex items-center justify-center px-4 sm:px-6"
            role="dialog"
            aria-modal="true"
            data-testid="unlink-google-modal"
        >
            <button
                aria-label="Fermer"
                onClick={onClose}
                className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            />
            <div className="relative tk-card max-w-md w-full p-6 sm:p-7 tk-menu-pop">
                <button
                    onClick={onClose}
                    disabled={working}
                    className="absolute top-4 right-4 text-[#86868B] hover:text-[#1D1D1F] disabled:opacity-40"
                    aria-label="Fermer"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 bg-[#F5F5F7]">
                    <Unlink className="w-5 h-5 text-[#1D1D1F]" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">
                    Délier votre compte Google ?
                </h3>
                <p className="mt-3 text-sm text-[#86868B] leading-relaxed">
                    Vous pourrez toujours vous connecter avec votre email et votre mot de passe. La connexion en un clic via Google sera désactivée jusqu&apos;à ce que vous la reliiez à nouveau.
                </p>
                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={working}
                        className="tk-btn tk-btn-secondary"
                        data-testid="unlink-google-modal-cancel"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={working}
                        className="tk-btn tk-btn-primary"
                        data-testid="unlink-google-modal-confirm"
                    >
                        {working ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" /> En cours…
                            </>
                        ) : (
                            "Délier Google"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
